import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Spinner,
  Alert,
  Container,
  Badge,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FiDownload, FiRefreshCw, FiFilter } from "react-icons/fi";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import Reportselect from "./Reportselect";
import Pagination from "../Constant/Pagination";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import { getAssetEndOfLifeReport } from "../../services/ReportServices";
import {
  searchcategoryOptionApi,
  searchDepartmentOptionApi,
} from "../../services/SearchService";

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Container className="mt-4">
          <Alert variant="danger">
            <h4>Something went wrong</h4>
            <p>{this.state.error?.message || "An unexpected error occurred"}</p>
            <Button
              variant="primary"
              onClick={() => this.setState({ hasError: false })}
            >
              Try Again
            </Button>
          </Alert>
        </Container>
      );
    }

    return this.props.children;
  }
}

const AssetEndOfLifeReport = () => {
  // Date range options
  const dateRangeOptions = [
    { label: "All", value: "all" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Next 7 Days", value: "7_days" },
    { label: "Next 15 Days", value: "15_days" },
    { label: "Next 30 Days", value: "30_days" },
    { label: "Last Month", value: "last_month" },
    { label: "Next 3 Months", value: "3_months" },
    { label: "Custom", value: "custom" },
  ];
  const getStatusBadges = (status) => {
  switch (status?.toUpperCase()) {
    case "IN_STOCK":
      return "secondary";  // Grey
    case "ALLOCATED":
      return "success";    // Green
    case "IN_REPAIR":
      return "warning";    // Yellow
    case "DISPOSED":
      return "danger";     // Red
    default:
      return "secondary";
  }
};


  // State for data
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // State for dropdowns
  const [dropdownCategory, setDropdownCategory] = useState(null);
  const [dropdownDepartment, setDropdownDepartment] = useState(null);
  const [dropdownCategoryOption, setDropdownCategoryOption] = useState([]);
  const [dropdownDepartmentOption, setDropdownDepartmentOption] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState(
    dateRangeOptions[0]
  );

  // State for filters
  const [param, setParam] = useState({
    page: 1,
    page_size: 10,
    query: "",
    category_id: "",
    department_id: "",
    date_range: "all",
    start_date: "",
    end_date: "",
  });

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      return d.toISOString().split("T")[0];
    } catch (e) {
      console.error("Invalid date format:", date);
      return "";
    }
  };

  // Initialize component
  useEffect(() => {
    const today = new Date();
    const start_date = formatDate(today);
    const end_date = formatDate(new Date(today.setDate(today.getDate() + 30)));

    setParam((prev) => ({
      ...prev,
      start_date,
      end_date,
    }));

    const loadFilterOptions = async () => {
      try {
        const [categoriesRes, departmentsRes] = await Promise.all([
          searchcategoryOptionApi(),
          searchDepartmentOptionApi(),
        ]);

        const categoryOpts =
          categoriesRes?.data?.results?.map((item) => ({
            label: item.label || item.name || "Unknown Category",
            value: item.value || item.id || "",
          })) || [];

        setDropdownCategoryOption(categoryOpts);

        const departmentOpts =
          departmentsRes?.data?.results?.map((item) => ({
            label: item.label || item.name || "Unknown Department",
            value: item.value || item.id || "",
          })) || [];

        setDropdownDepartmentOption(departmentOpts);
      } catch (error) {
        console.error("Failed to load filter options:", error);
        toast.error("Failed to load filter options");
      }
    };

    loadFilterOptions();
  }, []);

  // Handle date range selection
  useEffect(() => {
    if (selectedDateRange.value === "custom") return;

    const today = new Date();
    let startDate = "";
    let endDate = "";

    switch (selectedDateRange.value) {
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = formatDate(yesterday);
        endDate = formatDate(yesterday);
        break;
      case "7_days":
        startDate = formatDate(today);
        endDate = formatDate(new Date(today.setDate(today.getDate() + 7)));
        break;
      case "15_days":
        startDate = formatDate(today);
        endDate = formatDate(new Date(today.setDate(today.getDate() + 15)));
        break;
      case "30_days":
        startDate = formatDate(today);
        endDate = formatDate(new Date(today.setDate(today.getDate() + 30)));
        break;
      case "last_month":
        const firstDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        const lastDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        );
        startDate = formatDate(firstDayLastMonth);
        endDate = formatDate(lastDayLastMonth);
        break;
      case "3_months":
        startDate = formatDate(today);
        endDate = formatDate(new Date(today.setDate(today.getDate() + 90)));
        break;
      default:
        startDate = "";
        endDate = "";
    }

    setParam((prev) => ({
      ...prev,
      date_range: selectedDateRange.value,
      start_date: startDate,
      end_date: endDate,
      page: 1,
    }));
  }, [selectedDateRange]);

  // Fetch data when filters change
  useEffect(() => {
    const fetchTimer = setTimeout(() => {
      fetchData().catch(console.error);
    }, 300);
    return () => clearTimeout(fetchTimer);
  }, [param]);

  // Fetch report data
  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      if (
        param.date_range === "custom" &&
        (!param.start_date || !param.end_date)
      ) {
        throw new Error(
          "Please select both start and end dates for custom range"
        );
      }

      if (param.date_range === "custom") {
        const start = new Date(param.start_date);
        const end = new Date(param.end_date);

        if (start > end) {
          throw new Error("Start date cannot be after end date");
        }
      }

      const response = await getAssetEndOfLifeReport({
        ...param,
        export: false,
      });

      // Handle different response structures
      const responseData = response.data?.data || response.data?.results || [];
      const responseCount = response.data?.count || 0;

      if (!Array.isArray(responseData)) {
        throw new Error("Invalid data format received from server");
      }

      setReportData(responseData);
      setTotalCount(responseCount);
    } catch (err) {
      console.error("Error fetching report:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch asset end-of-life report";
      setError(errorMessage);
      toast.error(errorMessage);
      setReportData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle category change
  const handleCategoryChange = (selected) => {
    setDropdownCategory(selected);
    setParam((prev) => ({
      ...prev,
      category_id: selected?.value || "",
      page: 1,
    }));
  };

  // Handle department change
  const handleDepartmentChange = (selected) => {
    setDropdownDepartment(selected);
    setParam((prev) => ({
      ...prev,
      department_id: selected?.value || "",
      page: 1,
    }));
  };

  // Handle search change
  const handleSearchChange = (value) => {
    setParam((prev) => ({
      ...prev,
      query: value,
      page: 1,
    }));
  };

  // Handle page change
  const handlePageChange = (selectedPage) => {
    setParam((prev) => ({
      ...prev,
      page: selectedPage.selected + 1,
    }));
  };

  // Handle export
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await getAssetEndOfLifeReport({
        ...param,
        export: true,
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `asset_end_of_life_report_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export started successfully");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  // Get badge color based on days remaining
  const getStatusBadge = (daysRemaining) => {
    if (daysRemaining === null || daysRemaining === undefined)
      return "secondary";
    if (daysRemaining < 0) return "danger";
    if (daysRemaining < 30) return "warning";
    if (daysRemaining < 90) return "info";
    return "success";
  };

  // Format days remaining text
  const formatDaysRemaining = (days) => {
    if (days === null || days === undefined) return "N/A";
    if (days < 0) return `${Math.abs(days)} days overdue`;
    return `${days} days remaining`;
  };

  // Custom styles for dropdowns to prevent clipping
  const dropdownStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "35px",
      height: "35px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "35px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "35px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      marginTop: "2px",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "300px",
      overflowY: "auto",
    }),
  };

  return (
    <ErrorBoundary>
      <Container fluid>
        {/* Breadcrumb and header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="text-primary mb-0">Asset End of Life Report</h2>
            <ol className="breadcrumb breadcrumb-arrow mb-0">
              <li className="breadcrumb-item">
                <Link to="/">Dashboard</Link>
              </li>
              <li className="breadcrumb-item active">Asset End of Life</li>
            </ol>
          </div>
          <div
            className="d-flex align-items-center gap-2 ms-auto mt-2 mt-md-0"
            style={{ zIndex: 10 }}
          >
            <div style={{ width: "200px" }}>
              <Reportselect />
            </div>
            <Button
              variant="success"
              onClick={handleExport}
              disabled={exportLoading || loading}
              style={{ height: "41px" }}
            >
              {exportLoading ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <FiDownload className="me-1" />
              )}
              Export
            </Button>
          </div>
        </div>

        <Card style={{ minHeight: "350px" }}>
          {" "}
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h5 className="mb-0">
              <FiFilter className="me-2" /> Asset LifeEnd Status
            </h5>

            <div className="d-flex gap-2 flex-wrap align-items-center">
              <DebounceSmallSearchInput
                value={param.query}
                handleSearchChange={handleSearchChange}
              />

              <GlobalSearchableDropdown
                options={dropdownCategoryOption}
                selected={dropdownCategory}
                onSelect={handleCategoryChange}
                placeholder="Category"
                width="180px"
                menuPlacement="auto" // Auto position (top/bottom based on available space)
                menuPortalTarget={document.body} // Render dropdown in portal
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />

              <GlobalSearchableDropdown
                options={dropdownDepartmentOption}
                selected={dropdownDepartment}
                onSelect={handleDepartmentChange}
                placeholder="Department"
                width="180px"
                menuPlacement="auto"
                menuPortalTarget={document.body}
                styles={dropdownStyles}
              />

              <GlobalSearchableDropdown
                options={dateRangeOptions}
                selected={selectedDateRange}
                onSelect={(selected) =>
                  setSelectedDateRange(selected || dateRangeOptions[0])
                }
                isClearable={false}
                placeholder="Date Range"
                width="150px"
                menuPlacement="auto"
                menuPortalTarget={document.body}
                styles={dropdownStyles}
              />

              {selectedDateRange.value === "custom" && (
                <div style={{ width: "260px" }}>
                  <DatePicker
                    selectsRange
                    startDate={
                      param.start_date ? new Date(param.start_date) : null
                    }
                    endDate={param.end_date ? new Date(param.end_date) : null}
                    onChange={([start, end]) => {
                      setParam((prev) => ({
                        ...prev,
                        date_range: "custom",
                        start_date: start ? formatDate(start) : "",
                        end_date: end ? formatDate(end) : "",
                        page: 1,
                      }));
                    }}
                    isClearable
                    placeholderText="Select date range"
                    className="form-control form-control-sm"
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date(2000, 0, 1)}
                    maxDate={new Date(2100, 0, 1)}
                  />
                </div>
              )}
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {error && (
              <Alert variant="danger">
                {error}
                <Button variant="link" size="sm" onClick={fetchData}>
                  <FiRefreshCw /> Try again
                </Button>
              </Alert>
            )}

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading asset end-of-life report...</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <Table size="sm" className="align-middle text-nowrap small">
                    <thead className="table-light">
                      <tr>
                        <th>Asset ID</th>
                        <th>Asset Name</th>
                        <th>Category</th>
                        <th>Sub Category</th>
                        <th>Department</th>
                        <th>Purchase Date</th>
                        <th>Warranty End</th>
                        <th>Warranty Status</th>
                        <th>Lifespan (Months)</th>
                        <th>End of Life Date</th>
                        <th>Lifecycle Status</th>
                        <th>Current Cost</th>
                        <th>Asset Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.length === 0 ? (
                        <tr>
                          <td colSpan="13" className="text-center py-4">
                            <small>
                              No assets found matching your criteria
                            </small>
                          </td>
                        </tr>
                      ) : (
                        reportData.map((asset, index) => {
                          const assetData = asset || {};
                          return (
                            <tr key={index}>
                              <td>{assetData.asset_id || "-"}</td>
                              <td>{assetData.asset_name || "-"}</td>
                              <td>{assetData.category || "-"}</td>
                              <td>{assetData.sub_category || "-"}</td>
                              <td>{assetData.department || "-"}</td>
                              <td>
                                {formatDate(assetData.purchase_date) || "-"}
                              </td>
                              <td>
                                {formatDate(assetData.warranty_end_date) || "-"}
                              </td>
                              <td>
                                <Badge
                                  bg={getStatusBadge(
                                    assetData.warranty_days_remaining
                                  )}
                                >
                                  {formatDaysRemaining(
                                    assetData.warranty_days_remaining
                                  )}
                                </Badge>
                              </td>
                              <td>
                                {assetData.expected_lifespan_months || "-"}
                              </td>
                              <td>
                                {formatDate(assetData.end_of_life_date) || "-"}
                              </td>
                              <td>
                                <Badge
                                  bg={getStatusBadge(
                                    assetData.lifecycle_days_remaining
                                  )}
                                >
                                  {formatDaysRemaining(
                                    assetData.lifecycle_days_remaining
                                  )}
                                </Badge>
                              </td>
                              <td>
                                {assetData.current_cost
                                  ? `₹${parseFloat(
                                      assetData.current_cost
                                    ).toLocaleString("en-IN")}`
                                  : "-"}
                              </td>
                              <td>
                                <Badge bg={getStatusBadges(assetData.status)}>
                                  {assetData.status_display ||
                                    assetData.status ||
                                    "-"}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>

                {reportData.length > 0 && (
                  <Pagination
                    currentPage={param.page}
                    recordsPage={param.page_size}
                    dataLength={totalCount}
                    handlePagination={handlePageChange}
                    showPageInfo
                    disabled={loading}
                  />
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </ErrorBoundary>
  );
};

export default AssetEndOfLifeReport;
